import api from '../../API';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import WsiCard from '../../widgets/WsiCard';
import { Link } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import { Centered } from '../../layouts/Base';
import styles from './styles.module.css';
import { useAuth } from '../../providers/AuthContext';
import { queryClient } from '../../providers/AuthContext';
import { extractData, fetchApiQuery } from '../../utils/apiResponse';

function Collection(){
    const { user, pending } = useAuth();
    const revalidator = useRevalidator();
    const hasRevalidated = useRef(false);

    // The loader will access current settings 
    const {ok, collection, meta, folders, error} = useLoaderData();

    // Revalidate loader data when user logs in and we have an error
    useEffect(() => {
        if (!pending && user && user._id && !ok && !hasRevalidated.current) {
            hasRevalidated.current = true;
            revalidator.revalidate();
        }
    }, [user, pending, ok, revalidator]);

    // Reset hasRevalidated when data loads successfully
    useEffect(() => {
        if (ok) {
            hasRevalidated.current = false;
        }
    }, [ok]);

    // Show loading while revalidating
    if (pending || revalidator.state === 'loading' || (!ok && user && user._id && !hasRevalidated.current)) {
        return <Centered><div>Loading...</div></Centered>;
    }
    // const wsis = items.filter(item=>item.largeImage);
    // const awaitingUpload = items.filter(item=>item.awaitingUpload);
    // const data = {};
    
    if(ok){
        return (
            <Centered>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/collections">Collections</Link>
                <span aria-hidden="true">/</span>
                <span>{collection.name}</span>
            </nav>
            <h2 className={styles.pageTitle}>{collection.name}</h2>
            <div className={styles.cardList}>
                {folders.map(f => (
                    <div key={f._id} className={styles.folder}>
                        <Link to={`/folder/${f._id}`}>{f.name}</Link>
                    </div>
                ))}
            </div>
            </Centered>
        )
    } else {
        return (
            <Centered>
            <h1>Oops, there was a problem</h1>
            <p>{error}</p>
            </Centered>
        )
    }
    
}

function CollectionList(){
    const { user, pending } = useAuth();
    const revalidator = useRevalidator();
    const hasRevalidated = useRef(false);
    const {ok, data, error } = useLoaderData();

    // Revalidate loader data when user logs in and we have an error
    useEffect(() => {
        if (!pending && user && user._id && !ok && !hasRevalidated.current) {
            hasRevalidated.current = true;
            revalidator.revalidate();
        }
    }, [user, pending, ok, revalidator]);

    // Reset hasRevalidated when data loads successfully
    useEffect(() => {
        if (ok) {
            hasRevalidated.current = false;
        }
    }, [ok]);

    // Show loading while revalidating
    if (pending || revalidator.state === 'loading' || (!ok && user && user._id && !hasRevalidated.current)) {
        return <Centered><div>Loading...</div></Centered>;
    }
    if(ok){
        return (
            <Centered>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <span>Collections</span>
            </nav>
            <h2 className={styles.pageTitle}>Collections</h2>
            <div className={styles.cardList}>
                {data.map(c => (
                    <div key={c._id} className={styles.collectionCard}>
                        <Link to={`/collection/${c._id}`}>{c.name}</Link>
                    </div>
                ))}
            </div>
            </Centered>
        )
    } else {
        return (
            <Centered>
            <h1>Oops, there was a problem</h1>
            <p>{error}</p>
            </Centered>
        )
    }
}
/**
 * Loader for collection page
 * Uses React Query to prefetch and cache data
 */
async function Loader({params}){
    const { collectionId } = params;
    
    const [collectionResponse, foldersResponse] = await Promise.all([
        fetchApiQuery(queryClient, {
            queryKey: ['collection', collectionId],
            queryFn: () => api.get(`collection/${collectionId}`),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
        fetchApiQuery(queryClient, {
            queryKey: ['folders', 'parent', collectionId, 'collection'],
            queryFn: () => api.get(`folder`, {query: {'parentId': collectionId, 'parentType': 'collection'}}),
            staleTime: 5 * 60 * 1000,
        }).catch(() => null),
    ]);

    const collectionData = extractData(collectionResponse);
    const folders = extractData(foldersResponse);

    if (!collectionData) {
        return {
            ok: false,
            data: null,
            error: 'There was a problem fetching the collection.'
        };
    }

    return {
        ok: true,
        collection: collectionData,
        meta: collectionData.meta,
        folders: folders || [],
        error: !folders ? 'There was a problem fetching folders.' : null
    };
}

async function CollectionListLoader(){
    const response = await fetchApiQuery(queryClient, {
        queryKey: ['collections'],
        queryFn: () => api.get(`collection/`),
        staleTime: 5 * 60 * 1000,
    }).catch(() => null);

    const data = extractData(response);

    return {
        ok: !!data,
        data: data,
        error: data ? null : 'There was a problem contacting the DSA server.'
    };
}

export { Collection, Loader, CollectionList, CollectionListLoader };